import type { Track } from '../track/TrackLoader';
import type { GhostRecording, GhostStartState } from './Ghost';
import { FIXED_UPDATE_DT } from '../game/FrameTiming';

export interface TrainingGhostOptions {
  stepDt?: number;
  maxSpeed?: number;
  minSpeed?: number;
  startSpeed?: number;
  accel?: number;
  brake?: number;
  lookaheadMeters?: number;
}

const DEFAULT_STEP_DT = FIXED_UPDATE_DT;
const DEFAULT_MAX_SPEED = 74;
const DEFAULT_MIN_SPEED = 48;
const DEFAULT_START_SPEED = DEFAULT_MAX_SPEED;
const DEFAULT_ACCEL = 28;
const DEFAULT_BRAKE = 55;
const DEFAULT_LOOKAHEAD_METERS = 70;
const MAX_TRAINING_GHOST_SECONDS = 180;

/**
 * Build a deterministic onboarding rabbit that follows the track centerline
 * with a curvature-based speed profile. It is intentionally not a competitive
 * replay; once the player has a PB, normal PB/rival ghost selection takes over.
 */
export function buildTrainingGhost(
  track: Track,
  trackId: string,
  physicsVersion: string,
  options: TrainingGhostOptions = {},
): GhostRecording {
  const stepDt = options.stepDt ?? DEFAULT_STEP_DT;
  const maxSpeed = options.maxSpeed ?? DEFAULT_MAX_SPEED;
  const minSpeed = options.minSpeed ?? DEFAULT_MIN_SPEED;
  const accel = options.accel ?? DEFAULT_ACCEL;
  const brake = options.brake ?? DEFAULT_BRAKE;
  const lookaheadMeters = options.lookaheadMeters ?? DEFAULT_LOOKAHEAD_METERS;
  const start = sampleTrackAtArcLength(track, 0);
  let speed = clamp(options.startSpeed ?? DEFAULT_START_SPEED, minSpeed, maxSpeed);
  let progress = 0;
  let stepCount = 0;
  const stepLimit = Math.max(2, Math.ceil(MAX_TRAINING_GHOST_SECONDS / stepDt));

  // The procedural rabbit has no terrain data, so it stays on the v3 schema
  // and lets the live ground sampler place it at playback. y: NaN signals
  // "no recorded height" — resolveHeight in GhostShip falls back to the sampler.
  const startState: GhostStartState = {
    x: start.x,
    y: NaN,
    z: start.z,
    heading: start.heading,
    velocityX: start.tx * speed,
    velocityZ: start.tz * speed,
  };
  const states: GhostStartState[] = [];
  const throttle: number[] = [];
  const brakeInput: number[] = [];
  const steer: number[] = [];
  const onTrack: boolean[] = [];

  while (progress < track.totalLength && stepCount < stepLimit) {
    const here = sampleTrackAtArcLength(track, progress);
    const ahead = sampleTrackAtArcLength(track, progress + lookaheadMeters);
    const turn = smoothstep(angleDelta(here.heading, ahead.heading), 0.08, 0.9);
    const targetSpeed = lerp(maxSpeed, minSpeed, turn);
    const previousSpeed = speed;
    speed = approach(speed, targetSpeed, targetSpeed >= speed ? accel * stepDt : brake * stepDt);
    progress += speed * stepDt;

    states.push(sampleTrainingGhostState(track, progress, speed));
    throttle.push(speed >= previousSpeed ? 1 : 0);
    brakeInput.push(speed < previousSpeed ? 1 : 0);
    steer.push(angleDeltaSigned(here.heading, ahead.heading) / Math.PI);
    onTrack.push(true);
    stepCount += 1;
  }

  while (states.length < 2) {
    const nextProgress = Math.max(1, (states.length + 1) * speed * stepDt);
    states.push(sampleTrainingGhostState(track, nextProgress, speed));
    throttle.push(1);
    brakeInput.push(0);
    steer.push(0);
    onTrack.push(true);
  }

  return {
    schemaVersion: 3,
    trackId,
    lapMs: states.length * stepDt * 1000,
    physicsVersion,
    stepDt,
    startState,
    inputs: {
      throttle,
      brake: brakeInput,
      steer,
      onTrack,
    },
    states,
  };
}

export interface TrackArcSample {
  x: number;
  z: number;
  tx: number;
  tz: number;
  heading: number;
}

export function sampleTrackAtArcLength(track: Track, arcLength: number): TrackArcSample {
  const total = track.totalLength;
  const arc = total > 0 ? ((arcLength % total) + total) % total : 0;
  const cumulative = track.cumulativeLength;
  let lo = 0;
  let hi = cumulative.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (cumulative[mid] <= arc) lo = mid;
    else hi = mid - 1;
  }

  const a = track.centerline[lo];
  const b = track.centerline[(lo + 1) % track.centerline.length];
  const sx = b.x - a.x;
  const sz = b.z - a.z;
  const len = Math.hypot(sx, sz) || 1;
  const t = clamp((arc - cumulative[lo]) / len, 0, 1);
  const tx = sx / len;
  const tz = sz / len;

  return {
    x: a.x + sx * t,
    z: a.z + sz * t,
    tx,
    tz,
    heading: Math.atan2(tx, tz),
  };
}

export function sampleTrainingGhostState(
  track: Track,
  arcLength: number,
  speed = 0,
): GhostStartState {
  const sample = sampleTrackAtArcLength(track, arcLength);
  const safeSpeed = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  return {
    x: sample.x,
    y: NaN,
    z: sample.z,
    heading: sample.heading,
    velocityX: sample.tx * safeSpeed,
    velocityZ: sample.tz * safeSpeed,
  };
}

function approach(value: number, target: number, maxDelta: number): number {
  if (value < target) return Math.min(target, value + maxDelta);
  return Math.max(target, value - maxDelta);
}

function angleDelta(a: number, b: number): number {
  return Math.abs(angleDeltaSigned(a, b));
}

function angleDeltaSigned(a: number, b: number): number {
  return Math.atan2(Math.sin(b - a), Math.cos(b - a));
}

function smoothstep(x: number, edge0: number, edge1: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
